import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { API_BASE } from '../../utils/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatCurrency } from "../../utils/currency";
import { InputAdornment } from "@mui/material";

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import "./Price.css";

function Price() {
  const [tabIndex, setTabIndex] = useState(0);
  const [setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isCompactView, setIsCompactView] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/products?admin=true&limit=0`);
        console.log('Fetched products:', response.data);
        setProducts(response.data.products);
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const storedPrices = localStorage.getItem("editedPrices");
    if (storedPrices) {
      const parsedPrices = JSON.parse(storedPrices);
      const now = new Date().getTime();
      const oneDay = 24 * 60 * 60 * 1000;

      const filteredPrices = Object.keys(parsedPrices).reduce((acc, productId) => {
        acc[productId] = {};
        Object.keys(parsedPrices[productId]).forEach((variantId) => {
          if (now - parsedPrices[productId][variantId].timestamp < oneDay) {
            acc[productId][variantId] = parsedPrices[productId][variantId];
          }
        });
        return acc;
      }, {});

      setEditedPrices(filteredPrices);
    }
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewPriceChange = (productId, variantId, value, field) => {
    setEditedPrices((prev) => {
      const updatedPrices = {
        ...prev,
        [productId]: {
          ...prev[productId],
          [variantId]: {
            ...prev[productId]?.[variantId],
            [field]: value,
            timestamp: new Date().getTime(),
          },
        },
      };

      localStorage.setItem("editedPrices", JSON.stringify(updatedPrices));
      return updatedPrices;
    });
  };

  const handleSubmit = async () => {
    try {
      const updates = Object.keys(editedPrices).flatMap((productId) =>
        Object.keys(editedPrices[productId]).map((variantId) => {
          const product = products.find((p) => p._id === productId);
          const variant = product?.variants.find((v) => v._id === variantId);

          const newPriceValue = editedPrices[productId][variantId].newPrice;
          const newOfferPriceValue = editedPrices[productId][variantId].newOfferPrice;

          const priceToSend =
            newPriceValue !== undefined && newPriceValue !== null && newPriceValue !== ""
              ? parseFloat(newPriceValue)
              : variant?.price;

          let updateData = {
            productId,
            variantId,
            newPrice: priceToSend,
          };

          if (
            (newOfferPriceValue !== undefined && newOfferPriceValue !== null && newOfferPriceValue !== "") &&
            (newPriceValue === undefined || newPriceValue === null || newPriceValue === "")
          ) {
            const offer = parseFloat(newOfferPriceValue);
            if (offer >= variant.price) {
              toast.error(
                `Offer price for "${product?.productName}" (${variant?.quantity} ${variant?.unit}) cannot be higher than current price.`
              );
              throw new Error("Offer price too high");
            }
            updateData.newOfferPrice = offer;
            updateData.newPrice = variant.price;
          } else if (
            (newOfferPriceValue !== undefined && newOfferPriceValue !== null && newOfferPriceValue !== "") &&
            (newPriceValue !== undefined && newPriceValue !== null && newPriceValue !== "")
          ) {
            const offer = parseFloat(newOfferPriceValue);
            const price = parseFloat(newPriceValue);
            if (offer >= price || offer >= variant.price) {
              toast.error(
                `Offer price for "${product?.productName}" (${variant?.quantity} ${variant?.unit}) cannot be higher than new/current price.`
              );
              throw new Error("Offer price too high");
            }
            updateData.newOfferPrice = offer;
          } else {
            updateData.newOfferPrice = null;
          }
          return updateData;
        })
      );

      await axios.post(`${API_BASE}/api/products/update-prices`, updates);
      toast.success('Prices updated successfully!');
      localStorage.removeItem("editedPrices");
      setEditedPrices({});
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Error updating prices:', err);
      if (!err.message.includes("Offer price")) {
        toast.error('Failed to update prices.');
      }
    }
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const exportChanges = () => {
    const doc = new jsPDF();
    doc.text(`Price Change  - ${getCurrentDateTime()}`, 10, 10);

    const tableData = Object.keys(editedPrices).flatMap((productId) => {
      const product = products.find((p) => p._id === productId);

      if (!product) return [];

      return Object.keys(editedPrices[productId]).map((variantId) => {
        const variant = product.variants.find((v) => v._id === variantId);

        return [
          product.productNumber || '',
          product.productName || '',
          product.categoryName || '',
          product.subcategoryName || '',
          variant?.quantity || 'N/A',
          variant?.unit || 'N/A',
          variant?.price || 'N/A',
          variant?.offerPrice || 'N/A',
          editedPrices[productId][variantId]?.newPrice || '',
          editedPrices[productId][variantId]?.newOfferPrice || '',
        ];
      });
    });

    doc.autoTable({
      startY: 20,
      head: [['Product Number', 'Product Name', 'Category', 'Subcategory', 'Quantity', 'Unit', 'Old Price', 'Old Offer Price', 'New Price', 'New Offer Price']],
      body: tableData,
    });

    doc.save('Price_Change.pdf');
  };

  const exportAllPrices = () => {
    const doc = new jsPDF();
    doc.text(`All Prices  - ${getCurrentDateTime()}`, 10, 10);

    const tableData = products.flatMap((product) =>
      product.variants.map((variant) => [
        product.productNumber,
        product.productName,
        product.categoryName,
        product.subcategoryName,
        variant.quantity,
        variant.unit,
        variant.price,
        variant.offerPrice,
      ])
    );

    doc.autoTable({
      startY: 20,
      head: [['Product Number', 'Product Name', 'Category', 'Subcategory', 'Quantity', 'Unit', 'Price', 'Offer Price']],
      body: tableData,
    });

    doc.save('All_Prices.pdf');
  };

  const filteredProducts = Array.isArray(products)
    ? products.filter((product) => {
      const search = searchTerm.toLowerCase();
      return (
        product.productName.toLowerCase().includes(search) ||
        product.categoryName.toLowerCase().includes(search) ||
        product.subcategoryName.toLowerCase().includes(search)
      );
    })
    : [];

  const flattenData = filteredProducts.flatMap((product) =>
    product.variants.map((variant) => ({
      productId: product._id,
      variantId: variant._id,
      productNumber: product.productNumber,
      productName: product.productName,
      categoryName: product.categoryName,
      subcategoryName: product.subcategoryName,
      quantity: variant.quantity,
      unit: variant.unit,
      price: variant.price,
      offerPrice: variant.offerPrice,
      newPrice: editedPrices[product._id]?.[variant._id]?.newPrice || '',
      newOfferPrice: editedPrices[product._id]?.[variant._id]?.newOfferPrice || '',
      highlight: editedPrices[product._id]?.[variant._id] ? true : false,
    }))
  );

  const priceEditor = (rowData, field) => {
    const currencySymbol = formatCurrency(0).replace(/\d|[.,]/g, '');

    return (
      <TextField
        variant="filled"
        type="number"
        fullWidth
        value={rowData[field]}
        onChange={(e) =>
          handleNewPriceChange(rowData.productId, rowData.variantId, e.target.value, field)
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{currencySymbol}</InputAdornment>
          ),
        }}
      />
    );
  };


  const responsiveColumnHeader = (header, field, editor) => (rowData) => (
    <div className="responsive-cell">
      <span className="responsive-header">{header}</span>
      <span className="responsive-value">
        {editor ? editor(rowData, field) : rowData[field]}
      </span>
    </div>
  );


  return (
    <div>
      <ToastContainer position="top-center" autoClose={3000} />
      <h1>Price Update</h1>

      <div style={{ position: "sticky", top: 0, zIndex: 2, display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "200px",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            fontSize: "16px",
            marginRight: "10px"
          }}
        />
        <IconButton onClick={() => setIsCompactView(!isCompactView)}>
          {isCompactView ? <ViewListIcon /> : <ViewModuleIcon />}
        </IconButton>
      </div>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClick}
            sx={{ fontFamily: "Roboto", marginTop: "10px", minWidth: "100px", height: "40px" }}
          >
            Export
          </Button>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                exportChanges();
                handleClose();
              }}
              disabled={!Object.keys(editedPrices).length}
            >
              Export Changes
            </MenuItem>
            <MenuItem
              onClick={() => {
                exportAllPrices();
                handleClose();
              }}
            >
              Export All
            </MenuItem>
          </Menu>
        </Box>

        <Button
          variant="contained"
          color="primary"
          sx={{ fontFamily: "Roboto", marginTop: "10px", marginRight: '0.5cm', minWidth: "120px", height: "40px" }}
          onClick={handleSubmit}
          disabled={!Object.keys(editedPrices).length}
        >
          Update Prices
        </Button>
      </Box>

      <div style={{ border: "1px solid #ddd", borderRadius: "5px" }}>
        <DataTable
          value={flattenData}
          scrollable
          scrollHeight="450px"
          rowClassName={(rowData) => (rowData.highlight ? "highlight-row" : "")}
          responsiveLayout="stack"
          paginator
          rows={10}
          className="datatable-scrollable"
        >
          {/* Standard columns with responsive headers */}
          <Column
            field="productName"
            header="Product Name"
            body={responsiveColumnHeader("Product Name", "productName")}
          />
          {!isCompactView && (
            <Column
              field="categoryName"
              header="Category"
              body={responsiveColumnHeader("Category", "categoryName")}
            />
          )}
          {!isCompactView && (
            <Column
              field="subcategoryName"
              header="Subcategory"
              body={responsiveColumnHeader("Subcategory", "subcategoryName")}
            />
          )}
          {!isCompactView && (
            <Column
              field="quantity"
              header="Quantity"
              body={responsiveColumnHeader("Quantity", "quantity")}
            />
          )}
          {!isCompactView && (
            <Column
              field="unit"
              header="Unit"
              body={responsiveColumnHeader("Unit", "unit")}
            />
          )}
          <Column
            field="price"
            header="Current Price"
            body={responsiveColumnHeader("Current Price", "price", (rowData) => formatCurrency(rowData.price))}
          />
          <Column
            field="offerPrice"
            header="Offer Price"
            body={responsiveColumnHeader(
              "Offer Price",
              "offerPrice",
              (rowData) => formatCurrency(rowData.offerPrice)
            )}
          />

          <Column
            header="New Price"
            body={(rowData) => (
              <div className="responsive-cell">
                <span className="responsive-header">New Price</span>
                <span className="responsive-value">{priceEditor(rowData, "newPrice")}</span>
              </div>
            )}
          />
          <Column
            header="New Offer Price"
            body={(rowData) => (
              <div className="responsive-cell">
                <span className="responsive-header">New Offer Price</span>
                <span className="responsive-value">{priceEditor(rowData, "newOfferPrice")}</span>
              </div>
            )}
          />
        </DataTable>
      </div>

    </div>
  );
}

export default Price;